import axios from "axios";

export class Api {
    protected baseUrl: string = "/api/v1/";
    protected apiUrl: string = "";

    protected constructor(resource?: string) {
        this.apiUrl = this.baseUrl + resource + "/";
        let tokenStr = localStorage.getItem("access_token");
        if (tokenStr) {
            let token = JSON.parse(tokenStr);
            axios.defaults.headers.common["Authorization"] =
                token.token_type + " " + token.access_token;
        }
    }

    protected getResources(params?: Object): Promise<any> {
        return axios.get(this.apiUrl, { params: params });
    }

    /** Réponses de liste partagées, par URL + paramètres. */
    private static sharedLists: Map<
        string,
        { loadedAt: number; promise: Promise<any> }
    > = new Map();

    /**
     * GET de liste partagé entre les composants qui demandent la même URL
     * avec les mêmes paramètres pendant `ttlMs` : les sélecteurs et filtres
     * (clients, employés) rechargeaient chacun la liste complète à chaque
     * instance, sur chaque page.
     */
    protected getResourcesShared(
        params?: Object,
        ttlMs: number = 60 * 1000
    ): Promise<any> {
        const key = this.apiUrl + JSON.stringify(params || {});
        const cached = Api.sharedLists.get(key);
        if (cached && Date.now() - cached.loadedAt < ttlMs) {
            return cached.promise;
        }

        const promise = axios
            .get(this.apiUrl, { params: params })
            .catch(function (error) {
                Api.sharedLists.delete(key);
                throw error;
            });
        Api.sharedLists.set(key, { loadedAt: Date.now(), promise: promise });

        return promise;
    }

    /** Oublie les listes partagées (après une création ou une modification). */
    static forgetSharedLists(): void {
        Api.sharedLists.clear();
    }

    protected getResource(id: Number, params?: Object): Promise<any> {
        return axios.get(this.apiUrl + id, { params: params });
    }

    protected createResource(resource: any, params?: Object) {
        return axios.post(this.apiUrl, resource, { params: params });
    }

    protected updateResource(
        id: Number,
        resource: any,
        params?: Object
    ): Promise<any> {
        return axios.put(this.apiUrl + id, resource, { params: params });
    }

    protected saveResource(resource: any, params?: Object) {
        if (resource.id) {
            return this.updateResource(resource.id, resource, params);
        } else {
            return this.createResource(resource, params);
        }
    }

    /**
     * `raison` : motif saisi par l'utilisateur, consigné dans la corbeille
     * (cf. core/Modals/ConfirmSuppression).
     */
    protected deleteResource(id: Number, raison?: string) {
        return axios.delete(
            this.apiUrl + id,
            raison ? { data: { raison: raison } } : undefined
        );
    }

    /**
     * Ouvre dans un nouvel onglet un endpoint protégé par JWT en passant par un
     * blob (l'en-tête Authorization est porté par axios, pas par window.open).
     */
    openInNewTab(url: string): Promise<void> {
        return axios
            .get(url, { responseType: "blob" })
            .then(function (response) {
                let blobUrl = URL.createObjectURL(response.data);
                window.open(blobUrl, "_blank");
            });
    }
}

export namespace Api {
    export interface CallResponse {
        data: any;
        metadata: Array<any>;
        paginator: Paginator;
    }

    export interface Paginator {
        current_page: number;
        limit: number;
        total_count: number;
        total_pages: number;
    }
}
